import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { Email as EmailIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { cognitoServices } from '../services';

// Storage key for verified emails in localStorage
const VERIFIED_EMAILS_KEY = 'refscreens_verified_emails';

// Get verified emails from localStorage
const getVerifiedEmails = () => {
  try {
    const stored = localStorage.getItem(VERIFIED_EMAILS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save verified email to localStorage
const saveVerifiedEmail = (email) => {
  try {
    const emails = getVerifiedEmails();
    if (!emails.includes(email.toLowerCase())) {
      emails.push(email.toLowerCase());
      localStorage.setItem(VERIFIED_EMAILS_KEY, JSON.stringify(emails));
    }
  } catch (e) {
    console.error('Error saving verified email:', e);
  }
};

// Check if email is already verified locally
const isEmailVerifiedLocally = (email) => {
  const emails = getVerifiedEmails();
  return emails.includes(email.toLowerCase());
};

const EmailVerification = ({ 
  onVerified,
  onCancel,
  initialEmail = ''
}) => {
  const [step, setStep] = useState('email'); // 'email', 'code', 'verified'
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Check if email is already verified when component mounts or email changes
  useEffect(() => {
    if (initialEmail) {
      checkEmailVerified(initialEmail);
    }
  }, [initialEmail]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const checkEmailVerified = async (emailToCheck) => {
    if (!emailToCheck || !emailToCheck.includes('@')) return;
    
    setIsCheckingEmail(true);
    try {
      // Check localStorage for previously verified emails
      if (isEmailVerifiedLocally(emailToCheck)) {
        setStep('verified');
        onVerified(emailToCheck);
      }
    } catch (error) {
      console.error('Error checking email verification:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendCode = async () => {
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // First check if already verified locally
      if (isEmailVerifiedLocally(email)) {
        setStep('verified');
        onVerified(email);
        return;
      }

      // Sign up with Cognito (this sends the verification code)
      const response = await cognitoServices.sendVerificationCode(email);
      if (response.success) {
        if (response.alreadyVerified) {
          // Email was already verified in Cognito
          saveVerifiedEmail(email);
          setStep('verified');
          onVerified(email);
        } else {
          setStep('code');
          setCountdown(60); // 60 second cooldown before resend
        }
      } else {
        setError(response.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      setError(error.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await cognitoServices.verifyEmailCode(email, code);
      if (response.success) {
        // Save to localStorage so we don't need to verify again
        saveVerifiedEmail(email);
        setStep('verified');
        onVerified(email);
      } else {
        setError(response.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setError(null);
    setIsLoading(true);

    try {
      const response = await cognitoServices.resendVerificationCode(email);
      if (response.success) {
        setCountdown(60);
        setCode('');
      } else {
        setError(response.message || 'Failed to resend code');
      }
    } catch (error) {
      console.error('Error resending code:', error);
      setError(error.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'verified') {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Email Verified!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {email}
        </Typography>
      </Box>
    );
  }

  if (step === 'code') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Enter Verification Code
        </Typography>
        
        <Alert severity="info" sx={{ mb: 1 }}>
          We've sent a 6-digit verification code to <strong>{email}</strong>. 
          Please check your inbox (and spam folder).
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Verification Code"
          value={code}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(value);
            setError(null);
          }}
          placeholder="Enter 6-digit code"
          fullWidth
          inputProps={{ 
            maxLength: 6,
            style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' }
          }}
          disabled={isLoading}
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={handleResendCode}
            disabled={isLoading || countdown > 0}
            size="small"
          >
            {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
          </Button>
          
          <Button
            variant="text"
            onClick={() => {
              setStep('email');
              setCode('');
              setError(null);
            }}
            disabled={isLoading}
            size="small"
          >
            Change email
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleVerifyCode}
            disabled={isLoading || code.length !== 6}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </Button>
        </Box>
      </Box>
    );
  }

  // Email input step
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Verify Your Email
      </Typography>
      
      <Alert severity="info" sx={{ mb: 1 }}>
        To submit feedback, we need to verify your email address first. 
        This is a one-time verification.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <TextField
        label="Email Address"
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setError(null);
        }}
        placeholder="your.email@example.com"
        fullWidth
        disabled={isLoading || isCheckingEmail}
        InputProps={{
          endAdornment: isCheckingEmail ? <CircularProgress size={20} /> : null
        }}
      />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSendCode}
          disabled={isLoading || isCheckingEmail || !email}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
        >
          {isLoading ? 'Sending...' : 'Send Verification Code'}
        </Button>
      </Box>
    </Box>
  );
};

export default EmailVerification;
